/**
 * The ONE recording seam for governed tool invocations (#396).
 *
 * Two surfaces run registered tools — the MCP connector (external agents) and
 * the console copilot (the relay). The 07-31 review (SIM-14) found they already
 * interpret tools differently; a recording implementation in each would drift
 * the same way. So the seam lives here, next to `specToTool` (the shared bind
 * interpreter, moved here for the identical reason): ONE outcome classifier and
 * ONE writer contract, with each surface contributing only a thin call site and
 * its own RPC channel.
 *
 * What gets recorded is the TIER-1 structured signal only: tool name, surface,
 * outcome (ok / refused / error), duration, and a truncated failure reason.
 * NEVER the arguments, the prompt, or the response — data minimisation is the
 * design, not an omission (see the 2026080112 core migration). Identity is not
 * recorded here at all: `core.record_tool_invocation` derives the granted
 * principal from the caller's own token server-side (rule 11 — a recorder that
 * accepted an identity parameter would be an audit log of assertions).
 *
 * Recording is best-effort BY DESIGN: a failed write must never break the tool
 * call it records (the tool result is the user's work; the telemetry is ours).
 *
 * But best-effort is not SILENT (#2444). A failed record used to be swallowed
 * unless the caller happened to pass `onError` — and none of the three surfaces
 * did — so a recorder that failed on every call looked exactly like a surface
 * nobody used. A recorder now COUNTS its failures (`recorder.health()`) and, with
 * no `onError`, reports each one as an operational error line that names the
 * tool and the failure and nothing else.
 *
 * CORRELATION (#2444). `runRecorded` mints ONE id per tool call and hands it to
 * the run and to the record. A surface runs the tool on
 * `client.withCorrelation(id)` and records over a poster that sends the same id,
 * so the invocation row, every audit row and every receipt the call produced
 * resolve the same `correlation_id` server-side. The id is a join key the
 * client chooses; it authenticates nothing.
 */

export type InvocationSurface = 'mcp' | 'copilot'
export type InvocationOutcome = 'ok' | 'refused' | 'error'
export type ToolFailureCode =
  | 'authorization_refused'
  | 'validation_failed'
  | 'target_changed'
  | 'policy_changed'
  | 'target_unavailable'
  | 'dependency_unavailable'
  | 'internal_error'

export interface InvocationRecord {
  tool: string
  surface: InvocationSurface
  outcome: InvocationOutcome
  durationMs: number
  /** Refusal/error reason — a short message, never argument payloads. */
  reason?: string
  /** The id this call's requests carried (#2444). Sent as a header, never an argument. */
  correlationId?: string
  /** Optional governed proposal. Request correlation remains server-derived. */
  proposalId?: string | undefined
}

/** What a recorder has seen fail — observable, never thrown (#2444). */
export interface RecorderHealth {
  failures: number
  lastFailureAt: string | null
  lastError: string | null
}

/** `health` is present on every recorder `invocationRecorder` builds; optional
 *  only so a test double can still be a plain function. */
export type InvocationRecorder = ((rec: InvocationRecord) => Promise<void>) & {
  health?(): RecorderHealth
}

/**
 * The channel a surface already talks RPC over (SDK transport, raw fetch, …).
 * `correlationId`, when given, must travel as the `x-solum-correlation-id`
 * HEADER of that request — the server reads it from the request, never from
 * the arguments.
 */
export type RpcPoster = (fn: string, args: Record<string, unknown>, correlationId?: string) => Promise<unknown>

/** One correlation id per tool call. Uses the platform CSPRNG (browser and Node). */
export function newCorrelationId(): string {
  return globalThis.crypto.randomUUID()
}

/**
 * Refusal vs error, from the failure the surface already has in hand.
 * Refusals are the audit-relevant half: the governed stack says no through
 * PostgREST statuses (401/403), Postgres insufficient_privilege (42501),
 * row-level security with-check violations, the entitlement gate
 * ("module 'x' is not entitled"), and in-body authority guards
 * ("not authorized"). Everything else is an error (a bug, an outage, bad
 * input) — operationally interesting, but not a policy decision.
 */
const REFUSAL =
  /\b(?:401|403)\b|not entitled|not authori[sz]ed|not available to this caller|permission denied|insufficient_privilege|\b42501\b|row-level security|unknown tool|requires governed human confirmation/i

const SAFE_REFUSAL = Symbol.for('marble.safeToolRefusal')

/**
 * A refusal whose text WE wrote (#2558) — "unknown entity type 'organization';
 * did you mean core_organization?" — and which is therefore safe to hand the
 * agent verbatim. Everything else stays masked below, because a raw database
 * or network message can carry data. Construct it ONLY with text authored in
 * code from registry names and the caller's own arguments; never wrap an
 * error message you caught.
 */
export class SafeToolRefusal extends Error {
  readonly [SAFE_REFUSAL] = true
  constructor(message: string, readonly failureCode: ToolFailureCode = 'validation_failed',
    readonly outcome: 'refused' | 'error' = failureCode === 'authorization_refused' ? 'refused' : 'error') {
    super(message)
    this.name = 'SafeToolRefusal'
  }
}
const isSafeRefusal = (e: unknown): e is SafeToolRefusal =>
  typeof e === 'object' && e !== null && (e as Record<symbol, unknown>)[SAFE_REFUSAL] === true

export function classifyToolFailure(e: unknown): {
  outcome: 'refused' | 'error'
  reason: string
  failureCode: ToolFailureCode
} {
  if (isSafeRefusal(e)) return { outcome: e.outcome, failureCode: e.failureCode, reason: e.message }
  const msg = e instanceof Error ? e.message : String(e)
  if (REFUSAL.test(msg)) return {
    outcome: 'refused', failureCode: 'authorization_refused',
    reason: 'Authorization or policy refused the tool call.',
  }
  if (/\b(?:409|40001|40P01)\b|stale|conflict|changed after/i.test(msg)) return {
    outcome: 'error', failureCode: 'target_changed',
    reason: 'The target changed before the tool could finish.',
  }
  if (/\b(?:400|422|22023|23502|23503|23505|23514)\b|invalid|validation|restricted|nothing was sent/i.test(msg)) return {
    outcome: 'error', failureCode: 'validation_failed',
    reason: 'The tool call failed validation.',
  }
  if (/\b(?:404|P0002)\b|not found|unavailable target/i.test(msg)) return {
    outcome: 'error', failureCode: 'target_unavailable',
    reason: 'The target is no longer available.',
  }
  if (/\b(?:502|503|504|08000|08001|08006)\b|network|fetch failed/i.test(msg)) return {
    outcome: 'error', failureCode: 'dependency_unavailable',
    reason: 'A required dependency was unavailable.',
  }
  return { outcome: 'error', failureCode: 'internal_error', reason: 'The tool call failed unexpectedly.' }
}

/**
 * Build a recorder over the surface's own RPC channel. The write goes through
 * `core.record_tool_invocation` WITH THE CALLER'S TOKEN, so the row names the
 * granted principal and RLS/grants apply — recording is not a privileged path.
 *
 * A failure never breaks the recorded call, and is never silent: it is counted
 * in `health()` and handed to `onError` — or, with none, written as ONE
 * operational error line naming the tool and the failure (never arguments).
 */
export function invocationRecorder(post: RpcPoster, onError?: (e: unknown, rec: InvocationRecord) => void): InvocationRecorder {
  const health: RecorderHealth = { failures: 0, lastFailureAt: null, lastError: null }
  const report = onError ?? ((e: unknown, rec: InvocationRecord) => {
    const msg = e instanceof Error ? e.message : String(e)
    // eslint-disable-next-line no-console -- the operational record of a lost audit fact
    console.error(`[marble] tool-invocation recorder FAILED for ${rec.surface}:${rec.tool} — ${msg.slice(0, 300)}`)
  })
  const record = async (rec: InvocationRecord) => {
    try {
      await post('record_tool_invocation', {
        p_tool: rec.tool,
        p_surface: rec.surface,
        p_outcome: rec.outcome,
        p_duration_ms: Math.max(0, Math.round(rec.durationMs)),
        p_reason: rec.reason ?? null,
        // Omit for old-core compatibility: the five-argument recorder remains
        // the ordinary/copilot contract; only #2473 proposal calls use v2.
        ...(rec.proposalId ? { p_proposal_id: rec.proposalId } : {}),
      }, rec.correlationId)
    } catch (e) {
      health.failures += 1
      health.lastFailureAt = new Date().toISOString()
      health.lastError = (e instanceof Error ? e.message : String(e)).slice(0, 300)
      try { report(e, rec) } catch { /* a broken reporter must not break the tool call either */ }
    }
  }
  return Object.assign(record, { health: (): RecorderHealth => ({ ...health }) })
}

/**
 * Run one tool invocation through the seam: time it, record the outcome, and
 * hand the result (or the original failure) straight back. The record is
 * written BEFORE the failure is rethrown, so a refusal is on the log even when
 * the surface converts the throw into a protocol error afterwards.
 */
export async function runRecorded<T>(
  record: InvocationRecorder,
  tool: string,
  surface: InvocationSurface,
  run: (correlationId: string) => Promise<T>,
): Promise<T> {
  const t0 = Date.now()
  const correlationId = newCorrelationId()
  try {
    const out = await run(correlationId)
    await record({ tool, surface, outcome: 'ok', durationMs: Date.now() - t0, correlationId })
    return out
  } catch (e) {
    const { outcome, reason } = classifyToolFailure(e)
    await record({ tool, surface, outcome, reason, durationMs: Date.now() - t0, correlationId })
    throw e
  }
}
